import { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, X, Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import { useAiQuery } from '@/hooks/useAiQuery';
import { cn } from '@/lib/utils';

interface AiQueryBarProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onTaskClick?: (taskId: string) => void;
}

const SUGGESTIONS = [
  "What's on the critical path?",
  'Show overdue tasks',
  'Who is overloaded?',
  'Summarise project status',
  'What are the biggest schedule risks?',
];

export function AiQueryBar({
  projectId,
  isOpen,
  onClose,
  onTaskClick,
}: AiQueryBarProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { query, data, isPending, error, reset, recentQueries } =
    useAiQuery(projectId);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setInputValue('');
    reset();
    onClose();
  }, [onClose, reset]);

  const handleSubmit = useCallback(
    (queryText: string) => {
      const trimmed = queryText.trim();
      if (!trimmed || isPending) return;
      query(trimmed);
    },
    [query, isPending],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(inputValue);
      }
    },
    [inputValue, handleSubmit],
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setInputValue(suggestion);
      handleSubmit(suggestion);
    },
    [handleSubmit],
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  if (!isOpen) return null;

  const tasks = (data?.data as any)?.tasks as
    | Array<{
        id: string;
        name: string;
        percentComplete?: number;
        finishDate?: string;
      }>
    | undefined;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center pt-[10vh]',
        'bg-black/40 backdrop-blur-sm',
        'animate-in fade-in duration-200',
      )}
      onClick={handleBackdropClick}
    >
      <div
        ref={containerRef}
        className={cn(
          'w-full max-w-2xl mx-4',
          'bg-background/95 backdrop-blur-xl border border-border/50',
          'rounded-xl shadow-2xl',
          'animate-in slide-in-from-top-4 duration-300',
        )}
      >
        {/* Input area */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50">
          <Sparkles className="h-5 w-5 text-primary shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your project... (e.g., 'What's on the critical path?')"
            className={cn(
              'flex-1 bg-transparent text-xl font-light',
              'placeholder:text-muted-foreground/60',
              'outline-none border-none',
            )}
            disabled={isPending}
          />
          {isPending ? (
            <Loader2 className="h-5 w-5 text-muted-foreground animate-spin shrink-0" />
          ) : (
            <button
              onClick={handleClose}
              className="p-1 rounded-md hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Content area */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Loading shimmer */}
          {isPending && (
            <div className="p-4 space-y-3">
              <div className="h-4 bg-muted/60 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-muted/60 rounded animate-pulse w-1/2" />
              <div className="h-4 bg-muted/60 rounded animate-pulse w-2/3" />
            </div>
          )}

          {/* Error display */}
          {error && !isPending && (
            <div className="p-4">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive">
                    Failed to process query
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {error instanceof Error
                      ? error.message
                      : 'An unexpected error occurred. Please try again.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Response display */}
          {data && !isPending && (
            <div className="p-4 space-y-4">
              {/* Answer card */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border/30">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-4 w-4 text-primary mt-1 shrink-0" />
                  <p className="text-sm leading-relaxed">{data.answer}</p>
                </div>
              </div>

              {/* Task data table */}
              {tasks && tasks.length > 0 && (
                <div className="rounded-lg border border-border/30 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                          Task
                        </th>
                        <th className="text-right px-3 py-2 font-medium text-muted-foreground w-24">
                          Progress
                        </th>
                        <th className="text-right px-3 py-2 font-medium text-muted-foreground w-28">
                          Due
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => (
                        <tr
                          key={task.id}
                          className="border-t border-border/20 hover:bg-muted/20 cursor-pointer transition-colors"
                          onClick={() => onTaskClick?.(task.id)}
                        >
                          <td className="px-3 py-2">{task.name}</td>
                          <td className="px-3 py-2 text-right text-muted-foreground">
                            {task.percentComplete != null
                              ? `${Math.round(task.percentComplete)}%`
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground">
                            {task.finishDate
                              ? new Date(task.finishDate)
                                  .toISOString()
                                  .split('T')[0]
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Follow-up suggestions */}
              {data.suggestions && data.suggestions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-muted-foreground py-1">
                    Follow up:
                  </span>
                  {data.suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={cn(
                        'text-xs px-3 py-1.5 rounded-full',
                        'bg-primary/10 text-primary hover:bg-primary/20',
                        'transition-colors cursor-pointer',
                      )}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Default state: suggestions + recent */}
          {!data && !isPending && !error && (
            <div className="p-4 space-y-4">
              {/* Suggestion chips */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  Suggestions
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={cn(
                        'flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg',
                        'bg-muted/50 hover:bg-muted text-foreground',
                        'transition-colors cursor-pointer',
                        'border border-transparent hover:border-border/50',
                      )}
                    >
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent queries */}
              {recentQueries.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Recent</p>
                  <div className="space-y-1">
                    {recentQueries.map((recent) => (
                      <button
                        key={recent.timestamp}
                        onClick={() => handleSuggestionClick(recent.query)}
                        className={cn(
                          'w-full text-left text-sm px-3 py-2 rounded-lg',
                          'hover:bg-muted/50 transition-colors',
                          'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {recent.query}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Keyboard hint */}
              <div className="flex justify-center pt-2">
                <p className="text-xs text-muted-foreground/50">
                  Press{' '}
                  <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px] font-mono">
                    Enter
                  </kbd>{' '}
                  to query ·{' '}
                  <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px] font-mono">
                    Esc
                  </kbd>{' '}
                  to close
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AiQueryBar;
